"use client"

import { Bell, BookOpen } from "lucide-react"
import { createFileRoute } from "@tanstack/react-router"
import { T } from "@/i18n"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SetStateAction, useState } from "react"
import { TemplateForm } from "../(operations)/-components/template-form"
import { TemplateView } from "../(operations)/-components/template-view"
import { TriggerForm } from "../(operations)/-components/trigger-form"
import { TriggerView } from "../(operations)/-components/trigger-view"
import { DomainList, DomainListItemCard } from "@/components/domain"
import { useGetEmailTemplates, useDeleteEmailTemplate } from "@/hooks/email-templates"
import { useGetTriggers, useDeleteTrigger } from "@/hooks/triggers"
import { PageHeader } from "@/components/page-header"
import { BreadcrumbOptions } from "@/components/breadcrumbs"
import { RouterPaths } from "@/models/utils"

export const Route = createFileRoute("/(operations)/operations")({
  component: OperationsPage,
})

export function OperationsPage() {
  const [activeTab, setActiveTab] = useState<"templates" | "triggers">("templates")
  const [activeView, setActiveView] = useState<"list" | "edit" | "delete" | "new" | "view">("list")
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const { data: templates, isLoading: templatesLoading } = useGetEmailTemplates()
  const { data: triggers, isLoading: triggersLoading } = useGetTriggers()

  const handleAction = (tab: "templates" | "triggers", action: "delete" | "edit"| "new" | "view", id?: string) => {
    setActiveTab(tab)
    setActiveView(action)
    setSelectedId(id || null)
  }

  const handleBackToList = () => {
    setActiveView("list")
    setSelectedId(null)
  }

  const breadcrumbs : BreadcrumbOptions<RouterPaths>[] = [
    {
      content: (
        <>
          <T tKey="operations.title" /> <BookOpen width={16} height={16} />
        </>
      ),
      href: "/operations",
    },
  ]

  const renderDetailView = () => {
    switch (activeTab) {
      case "templates":
        switch (activeView) {
          case "edit":
            return <TemplateForm templateId={selectedId!} onBack={handleBackToList} onSuccess={handleBackToList} />
          case "view":
            return <TemplateView templateId={selectedId!} onBack={handleBackToList} />
          case "new":
            return <TemplateForm templateId={null} onBack={handleBackToList} onSuccess={handleBackToList}/>
        }
        break
      case "triggers":
        switch (activeView) {
          case "edit":
            return <TriggerForm triggerId={selectedId!} onBack={handleBackToList} onSuccess={handleBackToList} />
          case "view":
            return <TriggerView triggerId={selectedId!} onBack={handleBackToList} />
          case "new":
            return <TriggerForm triggerId={null} onBack={handleBackToList} onSuccess={handleBackToList}/>
        }
        break
    }
    return null
  }

  return (
    <>
      <PageHeader breadcrumbs={breadcrumbs as BreadcrumbOptions<RouterPaths>[]} />
      <div className="flex flex-1 flex-col gap-4 p-4">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as SetStateAction<"templates" | "triggers">)} className="w-full">
          {activeView == "list" && <div className="flex flex-col items-center mb-4">
            <TabsList className="mx-auto mb-4">
              <TabsTrigger value="templates">
                <BookOpen className="mr-2 h-4 w-4" />
                <T tKey="operations.tabs.templates" />
              </TabsTrigger>
              <TabsTrigger value="triggers">
                <Bell className="mr-2 h-4 w-4" />
                <T tKey="operations.tabs.triggers" />
              </TabsTrigger>
            </TabsList>
          </div>}

          {activeView === "list" ? (
            <>
              {activeTab === "templates" && (
                <DomainList
                  domain="templates"
                  data={templates}
                  isLoading={templatesLoading}
                  onNew ={() => handleAction("templates", "new")}
                  renderItem={(item, _index, isTable) => (
                    <OperationCard
                      key={item.id}
                      type="templates"
                      item={item}
                      isTable={isTable}
                      onView={(id) => handleAction("templates", "view", id)}
                      onEdit={(id) => handleAction("templates", "edit", id)}
                      onDelete={useDeleteEmailTemplate}
                    />
                  )}
                  hideNavActions={false}
                  noOutlet={true}
                />
              )}
              {activeTab === "triggers" && (
                <DomainList
                  domain="triggers"
                  data={triggers}
                  isLoading={triggersLoading}
                  onNew ={() => handleAction("triggers", "new")}
                  renderItem={(item, _index, isTable) => (
                    <OperationCard
                      key={item.id}
                      type="triggers"
                      item={item}
                      isTable={isTable}
                      onView={(id) => handleAction("triggers", "view", id)}
                      onEdit={(id) => handleAction("triggers", "edit", id)}
                      onDelete={useDeleteTrigger}
                    />
                  )}
                  hideNavActions={false}
                  noOutlet={true}
                />
              )}
            </>
          ) : (
            <div>{renderDetailView()}</div>
          )}
        </Tabs>
      </div>
    </>
  )
}

function OperationCard({
  type,
  item,
  isTable,
  onView,
  onEdit,
  onDelete,
}: {
  type: "templates" | "triggers"
  item: ({ entityType : string} | {subject : string}) & {name : string, id : string, createdAt: string, updatedAt : string}
  isTable: boolean
  onView: (id: string) => void
  onEdit: (id: string) => void
  onDelete: () => {mutateAsync : (x : string) => void}
}) {
  const getDescription = () => {
    switch (type) {
      case "triggers":
        return (item as { entityType : string}).entityType || "Unknown entity type" 
      default:
        return (item as {subject : string}).subject || "No description"
    }
  }
    const { mutateAsync } = onDelete();
    const deleteCard = () => mutateAsync(item.id.toString());

  return (
    <DomainListItemCard
      table={isTable}
      domain={type}
      title={item.name}
      description={getDescription()}
      viewLink={(children, key) => (
        <button key={key} onClick={() => onView(item.id)}>
          {children}
        </button>
      )}
      editLink={(children, key) => (
        <button key={key} onClick={() => onEdit(item.id)}>
          {children}
        </button>)}

      onDelete={deleteCard}

      contentMap={[
        { label: "Created", value: new Date(item.createdAt).toLocaleDateString() },
        { label: "Updated", value: new Date(item.updatedAt).toLocaleDateString() },
      ]}
    />
  )
}
